import { Pointer } from "rfc6902";
import { Control210, Control210Props } from "../../controls/Control210";
import { useArsContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { ARST_PATCH_DATA } from "../table";
import { useArsTable } from "../table/ArsTableContextProvider";

/**
 * Props for the ArsText component.
 */
export interface ArsTextProps extends Omit<Control210Props, "value" | "setValue"> {
  /** RFC 6901 JSON Pointer path to the data in ArsContext state */
  dataPath: string;
  /** Optional prop to explicitly indicate if the value is an array (default is auto-detected) */
  isArray?: boolean;
  /** Optional prop to indicate if the input should be multiline (textarea) */
  isMultiline?: boolean;
  /** Indicates if the dataPath is for table data */
  tableDataPath?: boolean;
}

/**
 * A text input control that reads from and writes to ArsContext state using a JSON Pointer path.
 * This is a thin wrapper around Control210 that integrates with ArsContext.
 * Uses PATCH_ARS_STATE action to update the state value at the specified dataPath.
 * Supports both single-line text input (default) and multi-line textarea via the `type` prop.
 *
 * The dataPath can point to either a string field or a string array field.
 * When the value is an array, it displays as newline-separated text in the textarea
 * and converts back to an array on change (with trim and empty line filtering).
 *
 * @param props - Component props extending Control210Props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the data in ArsContext state.
 * @param props.isArray - Optional flag to indicate if the value is an array (if not provided, it will be auto-detected).
 * @param props.isMultiline - Optional flag to indicate if the input should be multiline (textarea).
 * @param props.tableDataPath - Optional boolean indicating if the dataPath is for table data.
 * @returns A React functional component rendering a Control210 integrated with ArsContext.
 */
export const ArsText = ({
  dataPath,
  isArray = false,
  isMultiline = false,
  tableDataPath = false,
  ...rest
}: ArsTextProps): React.ReactElement => {
  const ars = useArsContext();
  const table = useArsTable();
  const sourceState = tableDataPath ? (table.state?.bodyData ?? {}) : ars.state;

  // Get the current value from state using the dataPath
  const pointer = Pointer.fromJSON(dataPath);
  const rawValue = pointer.get(sourceState) ?? (isArray ? [] : "");

  // Auto-detect if value is an array
  const isArrayValue = isArray || Array.isArray(rawValue);

  // Handle both string and string[] values
  // If it's an array, convert to newline-separated string for textarea display
  const value = isArrayValue ? (rawValue as string[]).join("\n") : (rawValue as string);

  /**
   * Handle input change by dispatching PATCH_ARS_STATE action.
   * If the original value was an array, convert newline-separated string back to array.
   *
   * @param newValue - The new value to set.
   */
  const handleChange = (newValue: string) => {
    let valueToSave: string | string[] | number | undefined;

    if (isArrayValue) {
      // Convert newline-separated string back to array
      valueToSave = newValue
        .split("\n")
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
    } else if (rest.type === "number") {
      const numValue = Number(newValue);
      valueToSave =
        newValue.trim() === "" || !isFinite(numValue)
          ? // istanbul ignore next
            undefined
          : numValue;
    } else {
      valueToSave = newValue;
    }

    if (!tableDataPath) {
      ars.dispatch({
        operation: PATCH_ARS_STATE,
        dataPath,
        value: valueToSave,
      });
    } else {
      table.dispatch({
        operation: ARST_PATCH_DATA,
        dataPath,
        value: valueToSave,
      });
    }
  };

  return (
    <Control210
      value={value}
      setValue={handleChange}
      type={isArrayValue || isMultiline ? "textarea" : rest.type}
      allowCarriageReturn={isArrayValue || isMultiline}
      {...rest}
    />
  );
};

ArsText.displayName = "ArsText";
